import React from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Button, CircularLoader, AlertBar } from '@dhis2/ui';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Given a section org unit, find its parent (department) record.
// Returns the parent org unit object, or null if it can't be determined.
const getParentDepartmentId = (unit, allUnits) => {
  if (!unit?.path) return null;

  // path looks like "/rootId/l5Id/l6Id/l7Id"
  const parts = unit.path.split('/').filter(Boolean);
  if (parts.length < 2) return null;

  const parentId = parts[parts.length - 2];
  const parent = allUnits.find(u => u.id === parentId);
  return parent || null;
};

 const groupByDepartment = (units, allUnits) => {
  const idSet = new Set(units.map(u => u.id));

  const findAncestorAtLevel = (unit, targetLevel) => {
    if (!unit?.path) return null;
    const parts = unit.path.split('/').filter(Boolean);
    for (const ancestorId of parts) {
      const ancestor = allUnits.find(u => u.id === ancestorId);
      if (!ancestor) continue;
      if (ancestor.level === targetLevel && idSet.has(ancestor.id)) {
        return ancestor;
      }
    }
    return null;
  };

  const groups = new Map();

  units.forEach(unit => {
    // Level 6 = department header
    if (unit.level === 6) {
      if (!groups.has(unit.id)) {
        groups.set(unit.id, { department: unit, sections: [] });
      }
      return;
    }

    // Levels 7 (section) and 8 (subsection) → nest under L6 ancestor
    const dept = findAncestorAtLevel(unit, 6);
    if (!dept) {
      if (!groups.has(unit.id)) {
        groups.set(unit.id, {
          department: { ...unit, name: `${unit.name} (unparented)` },
          sections: [],
        });
      }
      return;
    }

    if (!groups.has(dept.id)) {
      groups.set(dept.id, { department: dept, sections: [] });
    }
    groups.get(dept.id).sections.push(unit);
  });

  // Sort sections within each group: L7 first, then L8, alphabetically
  const result = Array.from(groups.values())
    .filter(g => g && g.department)
    .map(g => ({
      ...g,
      sections: [...g.sections].sort((a, b) => {
        if (a.level !== b.level) return a.level - b.level;
        return (a.name || '').localeCompare(b.name || '');
      }),
    }))
    .sort((a, b) =>
      (a.department?.name || '').localeCompare(b.department?.name || '')
    );

  console.log('🧩 [groupByDepartment] groups:', result.map(g => ({
    dept: g.department.name,
    sections: g.sections.map(s => `${s.name}(L${s.level})`),
  })));

  return result;
};

// Count how many times each name appears (case-insensitive, trimmed).
// Used to decide whether to show a parent hint next to a section name.
const hasDuplicateNames = (units) => {
  const counts = new Map();
  units.forEach(u => {
    const key = (u.name || '').trim().toLowerCase();
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return counts;
};

// ---------------------------------------------------------------------------
// SectionRow
// One draggable row for a single org unit (a section, or a department with
// no children). Rendered as a full-width row instead of a pill.
// ---------------------------------------------------------------------------
const SectionRow = ({
  section,
  parentName,
  isAssigned,
  showParentHint,
  onAssign,
  onUnassign,
  isDraggable = true,
}) => {
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'ORG_UNIT',
      item: { orgUnit: section },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
      canDrag: () => isDraggable,
    }),
    [section, isDraggable]
  );

  if (!section || !section.id) {
    console.warn('⚠️ [SectionRow] invalid section:', section);
    return null;
  }

  const isSubsection = section.level === 8;

  // Colour palette per level
  const palette = isSubsection
    ? {
        bg: isAssigned ? '#f1f8e9' : '#f7f7f7',      // pale green / pale grey
        border: isAssigned ? '#aed581' : '#e0e0e0',  // light green / light grey
        text: isAssigned ? '#33691e' : '#444',       // dark green / dark grey
        indent: 24,
        bullet: '↳',
      }
    : {
        bg: isAssigned ? '#e8f5e9' : '#f5f5f5',      // existing green / grey
        border: isAssigned ? '#4caf50' : '#ddd',
        text: isAssigned ? '#1b5e20' : '#333',
        indent: 0,
        bullet: isAssigned ? '✓' : '○',
      };

  return (
    <div
      ref={isDraggable ? drag : null}
      title={`${parentName || ''} › ${section.name || ''}`}
      onClick={() => {
        if (!isDraggable) return;
        isAssigned ? onUnassign(section.id) : onAssign(section.id);
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        paddingLeft: 12 + palette.indent,
        backgroundColor: palette.bg,
        border: `1px solid ${palette.border}`,
        borderRadius: 4,
        cursor: isDraggable ? 'move' : 'default',
        opacity: isDragging ? 0.5 : 1,
        transition: 'all 0.15s ease',
        fontSize: isSubsection ? 12.5 : 13,
      }}
    >
      <span
        style={{
          color: isSubsection ? palette.text : isAssigned ? '#2e7d32' : '#999',
          fontSize: isSubsection ? 12 : 14,
        }}
      >
        {palette.bullet}
      </span>

      <span style={{ color: palette.text, flex: 1 }}>
        {section.name || '(unnamed)'}
      </span>

      {isSubsection && (
        <span
          style={{
            fontSize: 10,
            padding: '1px 6px',
            backgroundColor: '#e8f5e9',
            color: '#2e7d32',
            borderRadius: 8,
            border: '1px solid #c8e6c9',
            whiteSpace: 'nowrap',
          }}
        >
          sub
        </span>
      )}

      {showParentHint && (
        <span
          style={{
            fontSize: 11,
            color: '#666',
            fontStyle: 'italic',
            opacity: 0.8,
          }}
        >
          ({parentName})
        </span>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// DepartmentGroup
// Renders a department header with a collapse/expand toggle and its sections
// nested beneath it.
// ---------------------------------------------------------------------------
const DepartmentGroup = ({
  department,
  sections,
  isAssigned,
  duplicateNameSet,
  onAssign,
  onUnassign,
}) => {
  const [expanded, setExpanded] = React.useState(false);

  if (!department || !department.name) {
    console.warn('⚠️ [DepartmentGroup] missing department:', department);
    return null;
  }

  const safeSections = Array.isArray(sections) ? sections : [];
  const sectionCount = safeSections.filter(s => s?.level === 7).length;
  const subsectionCount = safeSections.filter(s => s?.level === 8).length;

  const countLabel = isAssigned
    ? `${sectionCount} section${sectionCount !== 1 ? 's' : ''} assigned${
        subsectionCount > 0 ? ` · ${subsectionCount} sub` : ''
      }`
    : `${sectionCount} section${sectionCount !== 1 ? 's' : ''}${
        subsectionCount > 0 ? ` · ${subsectionCount} sub` : ''
      }`;

  const unwrap = (entry) => {
    if (!entry) return null;
    return entry.unit || entry;
  };

  return (
    <div style={{ marginBottom: 12 }}>
      {/* Department Header */}
      <div
        onClick={() => setExpanded(v => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          backgroundColor: '#eef4fa',
          borderRadius: 6,
          cursor: 'pointer',
          border: '1px solid #d0e0ef',
          userSelect: 'none',
        }}
      >
        <span
          style={{
            display: 'inline-block',
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s ease',
            fontSize: 12,
            color: '#1976d2',
          }}
        >
          ▶
        </span>

        <span style={{ fontWeight: 600, color: '#2c3e50', fontSize: 14, flex: 1 }}>
          🏢 {department.name}
        </span>

        {safeSections.length > 0 && (
          <span style={{ fontSize: 12, color: '#666' }}>{countLabel}</span>
        )}
      </div>

      {/* Section Rows */}
      {expanded && safeSections.length > 0 && (
        <div
          style={{
            marginTop: 4,
            marginLeft: 24,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          {safeSections.map((entry, idx) => {
            const unit = unwrap(entry);
            if (!unit || !unit.id) return null;
            return (
              <SectionRow
                key={unit.id || `sec-${idx}`}
                section={unit}
                parentName={department.name}
                isAssigned={isAssigned}
                showParentHint={duplicateNameSet?.has(
                  (unit.name || '').trim().toLowerCase()
                )}
                onAssign={onAssign}
                onUnassign={onUnassign}
              />
            );
          })}
        </div>
      )}

      {expanded && safeSections.length === 0 && (
        <div
          style={{
            marginTop: 4,
            marginLeft: 24,
            fontSize: 12,
            color: '#999',
            fontStyle: 'italic',
          }}
        >
          No sections {isAssigned ? 'assigned' : 'available'}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const OrganisationAssignDrop = ({
  programs,
  orgUnits,
  programAssignments,
  onAssignmentChange,
  onSaveAssignments,
  loading = false,
  currentUserOrgUnitId,
  showSaveButton = true,
  saveManagedByParent = false,
}) => {
  // ---- 1. Data prep (plain values) ----
  const filteredOrgUnits = orgUnits.filter(
    ou =>
      (ou.path?.includes(currentUserOrgUnitId) || ou.id === currentUserOrgUnitId) &&
      ou.level >= 6                 
  );

  const performancePrograms = programs.filter(
    program => program.name === 'Performance Management Monitoring'
  );

  // ---- 2. Helper functions ----
  const getAssignedOrgUnitsForProgram = (programId) => {
    const assignedIds = programAssignments[programId] || [];
    return filteredOrgUnits.filter(ou => assignedIds.includes(ou.id));
  };

  const getAllAssignedOrgUnits = () => {
    const allAssignedIds = new Set();
    Object.values(programAssignments).forEach(orgUnitIds => {
      orgUnitIds.forEach(id => allAssignedIds.add(id));
    });
    return filteredOrgUnits.filter(ou => allAssignedIds.has(ou.id));
  };

  const getUnassignedOrgUnits = () => {
    const allAssignedIds = new Set();
    Object.values(programAssignments).forEach(orgUnitIds => {
      orgUnitIds.forEach(id => allAssignedIds.add(id));
    });
    return filteredOrgUnits.filter(ou => !allAssignedIds.has(ou.id));
  };

  const handleProgramDrop = (programId, orgUnit, unassign = false) => {
    const newAssignments = { ...programAssignments };

    if (unassign) {
      Object.keys(newAssignments).forEach(pId => {
        newAssignments[pId] = newAssignments[pId].filter(id => id !== orgUnit.id);
        if (newAssignments[pId].length === 0) {
          delete newAssignments[pId];
        }
      });
    } else {
      Object.keys(newAssignments).forEach(pId => {
        if (pId !== programId) {
          newAssignments[pId] = newAssignments[pId].filter(id => id !== orgUnit.id);
          if (newAssignments[pId].length === 0) {
            delete newAssignments[pId];
          }
        }
      });
      newAssignments[programId] = [...(newAssignments[programId] || []), orgUnit.id];
    }

    onAssignmentChange(newAssignments);
  };

  // ---- 3. Derived values used by hooks ----
  const unassignedOrgs = getUnassignedOrgUnits();
  const assignedOrgs = getAllAssignedOrgUnits();
  const hasUnassigned = unassignedOrgs.length > 0;

  // ---- 4. ALL HOOKS — must run unconditionally ----
  const duplicateNameSet = React.useMemo(() => {
    const counts = hasDuplicateNames(filteredOrgUnits);
    const dupes = new Set();
    counts.forEach((count, name) => {
      if (count > 1) dupes.add(name);
    });
    return dupes;
  }, [filteredOrgUnits]);

  const unassignedGroups = React.useMemo(
    () => groupByDepartment(unassignedOrgs, filteredOrgUnits),
    [unassignedOrgs, filteredOrgUnits]
  );

  const assignedGroupsByProgram = React.useMemo(() => {
    const result = {};
    performancePrograms.forEach(program => {
      const assignedToProgram = getAssignedOrgUnitsForProgram(program.id);
      result[program.id] = groupByDepartment(assignedToProgram, filteredOrgUnits);
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programAssignments, performancePrograms, filteredOrgUnits]);

  // ---- 5. NOW the early return is safe ----
  if (performancePrograms.length === 0) {
    return (
      <AlertBar info>
        No Performance Management program found. Please create the program first.
      </AlertBar>
    );
  }

  // ---- 6. Render ----
  return (
    <DndProvider backend={HTML5Backend}>
      {/* Warning Alert for unassigned org units */}
      {hasUnassigned && (
        <AlertBar warning style={{ marginBottom: '16px' }}>
          ⚠️ {unassignedOrgs.length} organization unit(s) are not assigned to any
          program. Please assign all organization units before proceeding.
        </AlertBar>
      )}

      {/* Summary Stats */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '16px',
          padding: '8px',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
        }}
      >
        <span>
          Total Department/Sections Units:{' '}
          <strong>{filteredOrgUnits.length}</strong>
        </span>
        <span>
          Assigned:{' '}
          <strong style={{ color: '#4caf50' }}>{assignedOrgs.length}</strong>
        </span>
        <span>
          Unassigned:{' '}
          <strong style={{ color: hasUnassigned ? '#f44336' : '#4caf50' }}>
            {unassignedOrgs.length}
          </strong>
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '20px',
        }}
      >
        {/* Available Organization Units — grouped by department */}
        <div>
          <h4>Available Departments/Sections ({unassignedOrgs.length})</h4>
          <div
            style={{
              padding: '16px',
              backgroundColor: hasUnassigned ? '#fff3e0' : '#f8f9fa',
              borderRadius: '8px',
              minHeight: '200px',
              border: `2px solid ${hasUnassigned ? '#ff9800' : '#dee2e6'}`,
            }}
          >
            {unassignedGroups.map(({ department, sections }) => (
              <DepartmentGroup
                key={department.id}
                department={department}
                sections={sections}
                isAssigned={false}
                duplicateNameSet={duplicateNameSet}
                onAssign={(unitId) =>
                  handleProgramDrop(performancePrograms[0].id, { id: unitId })
                }
                onUnassign={(unitId) =>
                  handleProgramDrop(performancePrograms[0].id, { id: unitId }, true)
                }
              />
            ))}

            {unassignedOrgs.length === 0 && (
              <p style={{ color: '#28a745', fontStyle: 'italic', margin: 0 }}>
                All organization sections are assigned! ✓
              </p>
            )}
          </div>
        </div>

        {/* Program Assignment Zones — grouped by department */}
        <div>
          <h4>Performance Assignments ({assignedOrgs.length} assigned)</h4>
          {saveManagedByParent && (
            <AlertBar info style={{ marginBottom: '12px' }}>
              Changes made here will be saved when you click Update Company.
            </AlertBar>
          )}
          <p>
            {saveManagedByParent
              ? 'Use the Update Company button below to save these assignment changes together with the rest of the company updates.'
              : '⚠️ IMPORTANT: Ensure to press Save All Performance Assignment Button below after Assignment.'}
          </p>

          {performancePrograms.map(program => {
            const groups = assignedGroupsByProgram[program.id] || [];
            const assignedCount = getAssignedOrgUnitsForProgram(program.id).length;

            return (
              <div
                key={program.id}
                style={{
                  padding: '16px',
                  backgroundColor: '#fafafa',
                  border: `2px dashed ${hasUnassigned ? '#f44336' : '#bdbdbd'}`,
                  borderRadius: '8px',
                  marginBottom: '16px',
                }}
              >
                <h4 style={{ margin: '0 0 12px 0', color: '#1976d2' }}>
                  {program.name} {hasUnassigned && '⚠️'}
                </h4>

                {assignedCount === 0 ? (
                  <p style={{ color: '#757575', fontStyle: 'italic', margin: 0 }}>
                    {hasUnassigned
                      ? 'Missing assignments! Drag org units here'
                      : 'Drag organization units here to assign'}
                  </p>
                ) : (
                  groups.map(({ department, sections }) => (
                    <DepartmentGroup
                      key={department.id}
                      department={department}
                      sections={sections}
                      isAssigned={true}
                      duplicateNameSet={duplicateNameSet}
                      onAssign={(unitId) =>
                        handleProgramDrop(program.id, { id: unitId })
                      }
                      onUnassign={(unitId) =>
                        handleProgramDrop(program.id, { id: unitId }, true)
                      }
                    />
                  ))
                )}
              </div>
            );
          })}

          {/* Assignment Summary */}
          <div
            style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#e8f5e9',
              borderRadius: '4px',
            }}
          >
            <strong>Assignment Summary:</strong>
            {performancePrograms.map(program => {
              const assignedCount = getAssignedOrgUnitsForProgram(program.id).length;
              return (
                <div key={program.id} style={{ marginTop: '4px' }}>
                  {program.name}: <strong>{assignedCount}</strong> unit(s) assigned
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {showSaveButton && (
        <Button
          primary
          onClick={onSaveAssignments}
          disabled={loading || hasUnassigned}
          style={{
            marginTop: 20,
            width: '100%',
            backgroundColor: hasUnassigned ? '#ccc' : undefined,
          }}
        >
          {loading ? (
            <CircularLoader small />
          ) : hasUnassigned ? (
            `Assign ${unassignedOrgs.length} remaining unit(s)`
          ) : (
            'Save All Performance Assignments'
          )}
        </Button>
      )}
    </DndProvider>
  );
};

export default OrganisationAssignDrop;